import type { Block, Inline, Text } from "@contentful/rich-text-types";
import { createClient, type Entry } from "contentful";
import isUndefined from "lodash-es/isUndefined";
import type { ObservableMap } from "mobx";
import * as z from "zod";
import {
   APIPROXY_HOST,
   CONTENTFUL_ENV,
   CONTENTFUL_PREVIEWTOKEN,
   CONTENTFUL_READTOKEN,
   CONTENTFUL_SPACE
} from "../../common/environment";
import { logger } from "../../common/logger";
import { CONTENTFUL_PROXY_BASE_PATH } from "../../common/platformRoutes";
import type {
   ContentEntryType,
   ContentfulArticle,
   ContentfulEntry,
   ContentfulList,
   ContentfulRecipe,
   ContentfulTextAndImage,
   EntryTypesWithUrl,
   FocalPointData,
   ImageWithFocusData,
   MenuItem,
   UrlData,
   UrlDataWithoutFullUrl
} from "../../common/types/cmsTypes";
import type {
   TypeAdventCalendarSlotSkeleton,
   TypeFooterSkeleton,
   TypeProductCategorySkeleton,
   TypeRecipeSkeleton,
   TypeV2_articleSkeleton,
   TypeV2_sectionSkeleton,
   TypeWarningSkeleton
} from "../../common/types/contentful";
import {
   type AdventCalendarSlotUrlDataEntryType,
   type ArticleEntryType,
   type ArticleFundamentalEntryType,
   ArticlesInSection,
   type ArticleUrlDataEntryType,
   type FooterEntryType,
   type FullContentEntry,
   type ProductCategoryEntryType,
   type RecipeEntryType,
   type RecipeFundamentalEntryType,
   type RecipeUrlDataEntryType,
   type SectionContentEntryType,
   type SectionUrlDataEntryType,
   type WarningEntryType,
   ZAdventCalendarSlotUrlDataEntry,
   ZArticleEntry,
   ZArticleUrlDataEntry,
   ZFooterEntry,
   ZFullContentEntry,
   ZProductCategoryEntry,
   ZRecipeEntry,
   ZRecipeFundamentalEntry,
   ZRecipeUrlDataEntry,
   ZSectionUrlDataEntry,
   ZWarningEntry
} from "../../common/zodSchemas/contentful";

export const PREVIEW_BASE_PATH = "_preview";

const getContentfulProxyUrl = () => {
   if (!APIPROXY_HOST || APIPROXY_HOST === "undefined") {
      throw new Error("Missing APIPROXY_HOST configuration for Contentful proxy");
   }

   return new URL(APIPROXY_HOST);
};

export const contentfulClient = (isPreviewMode: boolean) =>
   isPreviewMode
      ? createClient({
           host: "preview.contentful.com",
           space: CONTENTFUL_SPACE ?? "",
           accessToken: CONTENTFUL_PREVIEWTOKEN ?? "",
           environment: CONTENTFUL_ENV
        })
      : createClient({
           space: CONTENTFUL_SPACE ?? "",
           accessToken: CONTENTFUL_READTOKEN ?? "",
           environment: CONTENTFUL_ENV
        });

export const contentfulClientCached = (isPreviewMode: boolean, cachingEnabled: boolean) =>
   isPreviewMode || !cachingEnabled
      ? contentfulClient(isPreviewMode)
      : (() => {
           const url = getContentfulProxyUrl();
           return createClient({
              host: url.host,
              basePath: CONTENTFUL_PROXY_BASE_PATH,
              insecure: url.protocol === "http:",
              space: CONTENTFUL_SPACE ?? "",
              accessToken: CONTENTFUL_READTOKEN ?? "",
              environment: CONTENTFUL_ENV
           });
        })();

export const getArticleSlugsAndParents = async (isPreviewMode: boolean, cachingEnabled: boolean) => {
   const articles = await contentfulClientCached(isPreviewMode, cachingEnabled)
      .withoutUnresolvableLinks.getEntries<TypeV2_articleSkeleton>({
         content_type: "v2_article",
         select: [
            "sys.contentType",
            "metadata.tags",
            "fields.title",
            "fields.slug",
            "fields.parentId",
            "fields.excludeSegments",
            "fields.includeSegments",
            "fields.visibleInSections"
         ],
         include: 1,
         limit: 1000
      })
      .then((response) => response.items);

   const validation = z.safeParse(z.array(ZArticleUrlDataEntry), articles);

   if (validation.success) {
      logger.log("contentful", "successful validation for article slugs and parents", articles);
   } else {
      logger.error("contentful", "validation failed for article slugs and parents", validation, articles);
   }

   return articles as ArticleUrlDataEntryType[];
};

export const getRecipeSlugsAndParents = async (isPreviewMode: boolean, cachingEnabled: boolean) => {
   const recipes = await contentfulClientCached(isPreviewMode, cachingEnabled)
      .withoutUnresolvableLinks.getEntries<TypeRecipeSkeleton>({
         content_type: "recipe",
         select: [
            "sys.contentType",
            "metadata.tags",
            "fields.title",
            "fields.slug",
            "fields.parentId",
            "fields.visibleInSections"
         ],
         include: 1,
         limit: 1000
      })
      .then((response) => response.items);

   const validation = z.safeParse(z.array(ZRecipeUrlDataEntry), recipes);

   if (validation.success) {
      logger.log("contentful", "successful validation for recipe slugs and parents", recipes);
   } else {
      logger.error("contentful", "validation failed for recipe slugs and parents", validation, recipes);
   }

   return recipes as RecipeUrlDataEntryType[];
};

export const getAdventCalendarSlotSlugsAndParents = async (isPreviewMode: boolean) => {
   const slots = await contentfulClient(isPreviewMode)
      .withoutUnresolvableLinks.getEntries<TypeAdventCalendarSlotSkeleton>({
         content_type: "adventCalendarSlot",
         select: ["sys.contentType", "metadata.tags", "fields.title", "fields.slug", "fields.parentId"],
         include: 1,
         limit: 1000
      })
      .then((response) => response.items);

   const validation = z.safeParse(z.array(ZAdventCalendarSlotUrlDataEntry), slots);

   if (validation.success) {
      logger.log("contentful", "successful validation for calendar slots slugs and parents", slots);
   } else {
      logger.error("contentful", "validation failed for calendar slots slugs and parents", validation, slots);
   }

   return slots as AdventCalendarSlotUrlDataEntryType[];
};

export const getArticlesInSection = async (sectionId: string, isPreviewMode: boolean, cachingEnabled: boolean) => {
   const articles = await contentfulClientCached(isPreviewMode, cachingEnabled)
      .getEntries<TypeV2_articleSkeleton>({
         content_type: "v2_article",
         "fields.visibleInSections": sectionId,
         select: [
            "fields.title",
            "fields.callToAction",
            "fields.callToActionSubTitle",
            "fields.preface",
            "fields.headerImage",
            "fields.orderPriority",
            "sys.contentType",
            "metadata.tags"
         ],
         include: 2
      })
      .then((response) => response.items);

   const validation = z.safeParse(ArticlesInSection, articles);

   if (validation.success) {
      logger.log("contentful", "successful validation for articles in section", articles);
   } else {
      logger.error("contentful", "validation failed for articles in section", validation, articles);
   }

   return articles as ArticleFundamentalEntryType[];
};

export const getRecipesInSection = async (
   sectionId: string,
   isPreviewMode: boolean,
   cachingEnabled: boolean
): Promise<RecipeFundamentalEntryType[]> => {
   const recipes = await contentfulClientCached(isPreviewMode, cachingEnabled)
      .getEntries<TypeRecipeSkeleton>({
         content_type: "recipe",
         "fields.visibleInSections": sectionId,
         select: ["fields.title", "fields.recipeData", "fields.orderPriority", "sys.contentType", "metadata.tags"],
         include: 0
      })
      .then((response) => response.items);

   const validation = z.safeParse(z.array(ZRecipeFundamentalEntry), recipes);

   if (validation.success) {
      logger.log("contentful", "successful validation for recipes in section", recipes);
   } else {
      logger.error("contentful", "validation failed for recipes in section", validation, recipes);
   }

   return recipes as RecipeFundamentalEntryType[];
};

export const getFooterContent = async (isPreviewMode: boolean) => {
   const footers = await contentfulClient(isPreviewMode)
      .withoutUnresolvableLinks.getEntries<TypeFooterSkeleton>({
         content_type: "footer",
         select: [
            "metadata",
            "fields.contactNumber",
            "fields.columnCustomerServiceLabel",
            "fields.openingHours",
            "fields.linksRichText",
            "fields.column2label",
            "fields.column2links",
            "fields.column3label",
            "fields.column3links",
            "fields.column4label",
            "fields.column4links",
            "fields.termsAndConditions"
         ],
         include: 1,
         limit: 1
      })
      .then((response) => response.items);

   const validation = z.safeParse(z.array(ZFooterEntry), footers);

   if (validation.success) {
      logger.log("contentful", "successful validation for footer content", footers);
   } else {
      logger.error("contentful", "validation failed for footer content", validation, footers);
   }

   return footers as FooterEntryType[];
};

export const getSectionsSlugsAndParents = async (isPreviewMode: boolean, cachingEnabled: boolean) => {
   const sections = await contentfulClientCached(isPreviewMode, cachingEnabled)
      .withoutUnresolvableLinks.getEntries<TypeV2_sectionSkeleton>({
         content_type: "v2_section",
         select: [
            "sys.contentType",
            "metadata.tags",
            "fields.title",
            "fields.slug",
            "fields.parentId",
            "fields.description",
            "fields.icon"
         ],
         include: 2
      })
      .then((response) => response.items);

   const validation = z.safeParse(z.array(ZSectionUrlDataEntry), sections);

   if (validation.success) {
      logger.log("contentful", "successful validation for section slugs and parents", sections);
   } else {
      logger.error("contentful", "validation failed for section slugs and parents", validation, sections);
   }

   return sections as SectionUrlDataEntryType[];
};

const _buildFullUrlFromParent = (page: ContentfulEntry): string => {
   const parent = page.fields.parent as ContentfulEntry | null;
   const slug = page.fields.slug as string;
   return parent ? `${_buildFullUrlFromParent(parent)}/${slug}` : slug;
};

const isArticleContent = (content: Entry | undefined): content is ContentfulArticle => {
   if (!content) {
      return false;
   }
   return content.sys.type === "Entry" && content.sys.contentType.sys.id === "v2_article";
};

const isListContent = (content: Entry | undefined): content is ContentfulList => {
   if (!content) {
      return false;
   }
   return content.sys.type === "Entry" && content.sys.contentType.sys.id === "callToActionList";
};

const isTextAndImageContent = (content: Entry | undefined): content is ContentfulTextAndImage => {
   if (!content) {
      return false;
   }
   return content.sys.type === "Entry" && content.sys.contentType.sys.id === "v2_textAndImage";
};

// Walks an article rich-text body and clears nested/linked article bodies so embedded content renders as teasers and avoids overly deep/cyclic graphs.
export const removeBodyBeyondNeededLevel = (content: ContentfulArticle) => {
   for (const child of content.fields.body.content) {
      // If the embedded entry is a list, we need to check each included item
      if (child.nodeType === "embedded-entry-block") {
         const embeddedEntry = child.data.target as ContentfulEntry;

         // If the embedded entry is an article, we don't need body to actually show teaser
         if (isArticleContent(embeddedEntry)) {
            embeddedEntry.fields.body.content = [];
         }

         // If the embedded entry is a list, drop any item that links back to the current article.
         if (isListContent(embeddedEntry) && embeddedEntry.fields.contentList) {
            embeddedEntry.fields.contentList = embeddedEntry.fields.contentList.filter(
               (listItem) => !(isArticleContent(listItem) && listItem.sys.id === content.sys.id)
            );
            for (const listItem of embeddedEntry.fields.contentList) {
               if (isArticleContent(listItem)) {
                  listItem.fields.body.content = [];
               }
            }
         }

         // If the embedded entry is a text and image content, we need to check its paragraphs and remove bodies from linked articles
         if (isTextAndImageContent(embeddedEntry) && embeddedEntry.fields.text) {
            const paragraphs = embeddedEntry.fields.text.content.filter((item) => item.nodeType === "paragraph");
            for (const paragraphEntry of paragraphs) {
               removeBodyFromLinksInParagraphs(paragraphEntry.content);
            }
         }
      }

      // If the child is a paragraph, we need to check for links and remove bodies from linked articles
      if (child.nodeType === "paragraph") {
         removeBodyFromLinksInParagraphs(child.content);
      }
   }

   return content;
};

const removeBodyFromLinksInParagraphs = (block: (Block | Inline | Text)[]) => {
   const links = block.filter((c) => c.nodeType === "entry-hyperlink");
   for (const link of links) {
      if (link.data?.target && isArticleContent(link.data.target)) {
         link.data.target.fields.body.content = [];
      }
   }
};

export const getFullContent = async (id: string, isPreviewMode: boolean, cachingEnabled: boolean) => {
   const content = await contentfulClientCached(isPreviewMode, cachingEnabled).getEntry<
      TypeV2_articleSkeleton | TypeV2_sectionSkeleton | TypeRecipeSkeleton | TypeAdventCalendarSlotSkeleton
   >(id, {
      include: 3
   });

   const validation = z.safeParse(ZFullContentEntry, content);

   if (validation.success) {
      logger.log("contentful", "successful validation for full content", content);
   } else {
      logger.error("contentful", "validation failed for full content", validation, content);
   }

   if (isArticleContent(content)) {
      removeBodyBeyondNeededLevel(content);
   }

   return content as FullContentEntry;
};

export const getFullArticleContentByIds = async (ids: string[], isPreviewMode: boolean): Promise<ArticleEntryType[]> => {
   const entries = await contentfulClient(isPreviewMode).withoutUnresolvableLinks.getEntries<TypeV2_articleSkeleton>({
      content_type: "v2_article",
      "sys.id[in]": ids
   });

   const articles = entries.items;

   const validation = z.safeParse(z.array(ZArticleEntry), articles);

   if (validation.success) {
      logger.log("contentful", "successful validation for full articles ", articles);
   } else {
      logger.error("contentful", "validation failed for full articles ", validation, articles);
   }

   return articles as ArticleEntryType[];
};

export const getFullRecipeContentByIds = async (ids: string[], isPreviewMode: boolean): Promise<RecipeEntryType[]> => {
   const entries = await contentfulClient(isPreviewMode).getEntries<TypeRecipeSkeleton>({
      content_type: "recipe",
      "sys.id[in]": ids
   });

   const recipes = entries.items;

   const validation = z.safeParse(z.array(ZRecipeEntry), recipes);
   if (validation.success) {
      logger.log("contentful", "successful validation for full recipes ", recipes);
   } else {
      logger.error("contentful", "validation failed for full recipes ", validation, recipes);
   }
   return recipes as RecipeEntryType[];
};

export const getUnprocessedUrlDataFromContent = (content: SectionContentEntryType): UrlDataWithoutFullUrl => {
   const baseData = {
      id: content.sys.id,
      title: content.fields?.title ?? "Uten tittel",
      slug: content.fields?.slug ?? content.sys.id,
      type: content.sys.contentType.sys.id as EntryTypesWithUrl,
      parentId: content.fields?.parentId
   };

   if (content.fields && "visibleInSections" in content.fields) {
      return {
         ...baseData,
         visibleInSections: content.fields.visibleInSections as string[]
      };
   }

   return baseData;
};

export const buildFullUrlRecursively = (
   builtUrl: string,
   parentId: string | undefined,
   allPages: UrlDataWithoutFullUrl[]
): string => {
   if (isUndefined(parentId)) {
      return builtUrl;
   }
   const parent = allPages.find((possibleParent) => possibleParent.id === parentId);
   if (isUndefined(parent)) {
      return builtUrl;
   }
   return buildFullUrlRecursively(`${parent.slug}/${builtUrl}`, parent.parentId, allPages);
};

export const generateFullUrl = (page: UrlDataWithoutFullUrl, allPages: UrlDataWithoutFullUrl[]): UrlData => {
   const fullUrl = buildFullUrlRecursively(page.slug, page.parentId, allPages);
   return {
      ...page,
      fullUrl
   };
};

export const getWarnings = async (isPreviewMode: boolean): Promise<WarningEntryType[]> => {
   const entries = await contentfulClient(isPreviewMode).withoutUnresolvableLinks.getEntries<TypeWarningSkeleton>({
      content_type: "warning",
      include: 1
   });

   const warnings = entries.items;

   const validation = z.safeParse(z.array(ZWarningEntry), warnings);
   if (validation.success) {
      logger.log("contentful", "successful validation for warnings", warnings);
   } else {
      logger.error("contentful", "validation failed for warnings", validation, warnings);
   }

   return warnings as WarningEntryType[];
};

/**
 * Returns true if the content is tagged with the appriate tag to indicate that the content
 * should be featured in the main menu
 */
export const hasMainMenuTag = (content: SectionContentEntryType) => {
   const MAIN_MENU_TAG = "visibleInMainMenu";
   return (content.metadata?.tags?.filter((tag) => tag.sys.id === MAIN_MENU_TAG).length ?? 0) > 0;
};

export const createMenuItem = (content: SectionContentEntryType, allUrlData: UrlData[]): MenuItem[] => {
   // Is this content even tagged to be featured in the main menu?
   if (!hasMainMenuTag(content)) {
      return [];
   }

   const urlForMenuItem = allUrlData.find((urlData) => urlData.id === content.sys.id)?.fullUrl;
   // If we are unable to generate a proper URL for the content, we skip it
   if (isUndefined(urlForMenuItem)) {
      return [];
   }

   return [
      {
         title: content.fields.title,
         id: content.sys.id,
         url: urlForMenuItem.startsWith("/") ? urlForMenuItem : `/${urlForMenuItem}`,
         contentType: content.sys.contentType.sys.id as ContentEntryType
      }
   ];
};

export const getProductCategoryContent = async (isPreviewMode: boolean): Promise<ProductCategoryEntryType[]> => {
   const entry = await contentfulClient(isPreviewMode).withoutUnresolvableLinks.getEntries<TypeProductCategorySkeleton>({
      content_type: "productCategory",
      include: 3
   });

   const productCategories = entry.items as ProductCategoryEntryType[];
   const validation = z.safeParse(ZProductCategoryEntry.array(), productCategories);
   if (validation.success) {
      logger.log("contentful", "successful validation for product category content", productCategories);
   } else {
      logger.error("contentful", "validation failed for product category content", validation, productCategories);
   }
   // Sort items ordered by 1)segmented include, 2)segmented exclude, 3) non-segmented.
   // Product category page displays only the first item.
   return productCategories.sort((item1, item2) => {
      if (item1.fields.includeSegments && !item2.fields.includeSegments) {
         return -1;
      }
      if (!item1.fields.includeSegments && item2.fields.includeSegments) {
         return 1;
      }
      if (item1.fields.excludeSegments && !item2.fields.excludeSegments) {
         return -1;
      }
      if (!item1.fields.excludeSegments && item2.fields.excludeSegments) {
         return 1;
      }

      return 0;
   });
};

const sectionEntryComparator = (
   entry1: ArticleFundamentalEntryType | ContentfulRecipe,
   entry2: ArticleFundamentalEntryType | ContentfulRecipe
) => {
   const orderPriority1 = entry1.fields.orderPriority || 5;
   const orderPriority2 = entry2.fields.orderPriority || 5;
   return orderPriority1 - orderPriority2;
};
export const sortSectionContentEntries = (entries: ArticleFundamentalEntryType[] | ContentfulRecipe[]) => {
   return entries.sort(sectionEntryComparator);
};

type KV = { key: string; value: string };

export const toKeyValue = (u: unknown): KV | undefined => {
   if (u && typeof u === "object" && "key" in u && "value" in u) {
      const { key, value } = u;
      if (typeof key === "string" && typeof value === "string") {
         return { key, value };
      }
   }
   return undefined;
};

export const toKeyValueArray = (ua: unknown): KV[] => {
   if (Array.isArray(ua)) {
      return ua.map((u) => toKeyValue(u)).filter((u) => u) as KV[];
   }
   return [];
};

export const cacheImageWithFocusData = (
   cache: ObservableMap<string, ImageWithFocusData>,
   content: (ArticleFundamentalEntryType | RecipeFundamentalEntryType)[]
) => {
   for (const articleOrRecipe of content) {
      const { fields } = articleOrRecipe;

      if ("headerImage" in fields) {
         const id = fields.headerImage?.sys?.id;
         if (id) {
            const data = fields.headerImage?.fields;
            const type = articleOrRecipe.sys?.contentType.sys.id;

            if (data && type) {
               const focalPoint: FocalPointData = data.focalPoint as FocalPointData;
               cache.set(id, {
                  entryId: id,
                  altText: data.altText,
                  focalPoint: focalPoint.focalPoint,
                  originalAssetUrl: data.image?.fields.file?.url
               });
            }
         }
      }
   }
};
